import { useContext, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getCurrentConversation } from '../../../../reducers/aiAssistant.selectors';
import { aiAssistantContext } from '../../../../state/AIAssistantContextProvider';
import { DEFAULT_DIALOG_FOOTER_HEIGHT } from '../../shared/Footer/AIAssistantFooter';

/**
 * Custom hook to calculate height of the latest message container
 */
export const useAIAssistantLatestMessageHeight = (): number => {
  const {
    dialogContentRef,
    lastUserMessageRef,
    dialogMessagesContainerRef,
    dialogFooterRef
  } = useContext(aiAssistantContext);
  const currentConversation = useSelector(getCurrentConversation);
  const [messageHeight, setMessageHeight] = useState(0);

  const messagesLength = currentConversation?.data?.messages.length;

  useEffect(() => {
    const calculateHeight = () => {
      if (
        !dialogContentRef?.current
        || !lastUserMessageRef?.current
        || !dialogMessagesContainerRef?.current
        || !dialogFooterRef?.current
      ) {
        return;
      }

      const dialogContent = dialogContentRef.current;
      const lastUserMessage = lastUserMessageRef.current;
      const messagesContainer = dialogMessagesContainerRef.current;
      const dialogFooter = dialogFooterRef.current;

      // Get computed styles
      const dialogStyles = getComputedStyle(dialogContent);
      const userMessageStyles = getComputedStyle(lastUserMessage);
      const containerStyles = getComputedStyle(messagesContainer);

      // Calculate available height
      const dialogContentHeight = dialogContent.offsetHeight;
      const userMessageHeight = lastUserMessage.offsetHeight;
      const dialogFooterHeight = dialogFooter.offsetHeight;
      const userMessageMargin = parseInt(userMessageStyles.marginBottom);
      const dialogPadding = parseInt(dialogStyles.paddingBottom);
      const containerPadding = parseInt(containerStyles.paddingTop) + parseInt(containerStyles.paddingBottom);

      const calculatedHeight = dialogContentHeight
        + (dialogFooterHeight - DEFAULT_DIALOG_FOOTER_HEIGHT)
        - userMessageHeight
        - userMessageMargin
        - dialogPadding
        - containerPadding;

      setMessageHeight(calculatedHeight);
    };

    calculateHeight();

    // Recalculate on window resize
    window.addEventListener('resize', calculateHeight);
    return () => window.removeEventListener('resize', calculateHeight);

  }, [
    dialogContentRef,
    lastUserMessageRef,
    dialogMessagesContainerRef,
    dialogFooterRef,
    messagesLength
  ]);

  return messageHeight;
};
